import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGameSounds } from '../../../../shared/hooks/useSound';
import { clickCounter } from '../../../../core/services/EventTracker';
import { transactionTracker } from '../../../../core/services/TransactionTracker';
import { useDeviceMode, useViewMode } from '../../../../store/app/appStore';
import { testIds } from '../../../../core/testIds';
import { JACKPOT_STATISTICS_SORT } from '../../constants';
import { useJackpotWins } from '../../hooks/useJackpotWins';
import {
  normalizeJackpotLevelName,
  selectDisplayLevels,
  selectHeaderWin,
  selectVisibleWins,
} from '../../utils/jackpotStatistics';
import type { IJackpotWinRow, JackpotStatisticsSortMode } from '../../types/jackpotStatistics.types';
import JackpotStatisticsHeader from './JackpotStatisticsHeader';
import JackpotStatisticsContent from './JackpotStatisticsContent';
import JackpotStatisticsFooter from './JackpotStatisticsFooter';
import styles from './JackpotStatistics.module.css';

/** What Tab can land on inside the drawer: the footer's chips, ordering buttons and close, and the rows' launch buttons. */
const FOCUSABLE_SELECTOR = 'button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The share of the viewport browsers scroll for Space / Page Down. */
const PAGE_SCROLL_FRACTION = 0.875;

export interface JackpotStatisticsDrawerProps {
  /** Jackpot instance name, e.g. 'Bell Link' — also the `instanceName` the win stats are for. */
  jackpotName: string;
  /** Section the drawer was opened from — test ids only. */
  sectionName: string;
  /**
   * True while a game launch is already in flight — blocks the rows' launch buttons. Injected
   * (with `onStartGame`) by the layout: the games feature consumes jackpot data, so the drawer
   * importing the games feature back would close an import cycle.
   */
  isLaunchBlocked: boolean;
  /** The games feature's launch action, injected by the layout for the same reason. */
  onStartGame: (gameKey: string) => void;
  onClose: () => void;
}

/**
 * Jackpot statistics drawer — a portal-rendered overlay listing the jackpot's recent wins,
 * opened by tapping a level on the jackpot panel. Wins can be filtered by level and sorted
 * (last 100 / top wins).
 */
const JackpotStatisticsDrawer: FC<JackpotStatisticsDrawerProps> = ({
  jackpotName,
  sectionName,
  isLaunchBlocked,
  onStartGame,
  onClose,
}) => {
  const [sortMode, setSortMode] = useState<JackpotStatisticsSortMode>(JACKPOT_STATISTICS_SORT.LAST_100);
  const [selectedLevelName, setSelectedLevelName] = useState<string | null>(null);

  const { data: winsData, isLoaded } = useJackpotWins(jackpotName);

  const { playSelectGameSound } = useGameSounds();
  const viewMode = useViewMode();

  const deviceMode = useDeviceMode();
  const levels = useMemo(() => selectDisplayLevels(winsData.levels), [winsData.levels]);

  const visibleWins = useMemo(
    () => selectVisibleWins(levels, sortMode, selectedLevelName),
    [levels, sortMode, selectedLevelName],
  );

  const headerWin = useMemo(
    () => selectHeaderWin(levels, sortMode, selectedLevelName),
    [levels, sortMode, selectedLevelName],
  );

  // A refetch can drop the level the player is filtering by; its chip goes with it, so the player
  // could not clear the filter themselves — drop it here rather than leave a dead filter behind.
  useEffect(() => {
    if (selectedLevelName === null) return;

    const stillPresent = levels.some((level) => normalizeJackpotLevelName(level.levelName) === selectedLevelName);
    if (!stillPresent) setSelectedLevelName(null);
  }, [levels, selectedLevelName]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Single-select: picking a level replaces whatever was selected, picking the selected one
  // again clears the filter and returns to the initial "all levels" state. The footer emits the
  // already-normalised level name.
  const handleToggleLevel = useCallback((levelName: string) => {
    setSelectedLevelName((current) => (current === levelName ? null : levelName));
  }, []);

  const handleSelectSort = useCallback((mode: JackpotStatisticsSortMode) => {
    setSortMode(mode);
    scrollerRef.current?.scrollTo({ top: 0 });
  }, []);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const drawerRef = useRef<HTMLDivElement | null>(null);

  // Keyboard stays inside the drawer: Tab wraps around its own controls instead of walking into
  // the (covered) lobby, Escape closes like any dialog, and Space only ever scrolls the list.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === ' ') {
        // Space means "scroll the list" anywhere in the drawer, never "activate the focused
        // button" — Enter does that. Otherwise a chip that has just been clicked (and so holds
        // focus) toggles itself back off under the very key pressed to scroll.
        const scroller = scrollerRef.current;
        if (scroller && e.target !== scroller) {
          e.preventDefault();
          scroller.scrollBy({ top: (e.shiftKey ? -1 : 1) * scroller.clientHeight * PAGE_SCROLL_FRACTION });
        }
        return;
      }
      if (e.key !== 'Tab' || !drawerRef.current) return;

      const focusable = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !drawerRef.current.contains(active);

      if (e.shiftKey && (outside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (outside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (e.key === ' ' && e.target !== scrollerRef.current) e.preventDefault();
  }, []);

  // The level that opened the drawer is a plain <div>, so focus would otherwise stay on the
  // lobby and Space / the arrow keys would keep scrolling it behind the overlay. Focusing the
  // list makes those keys scroll the list instead; focus goes back where it was on close.
  useEffect(() => {
    if (!isLoaded) return undefined;

    const previouslyFocused = document.activeElement;
    scrollerRef.current?.focus({ preventScroll: true });

    return () => {
      if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isLoaded]);

  const handleSelectGame = useCallback(
    (win: IJackpotWinRow) => {
      if (isLaunchBlocked) return;

      clickCounter.incrementClick(`click_jackpot_statistics_game_${win.gameKey}`);
      transactionTracker.trackGameOpen(win.gameName, win.gameKey, sectionName, viewMode);
      playSelectGameSound();
      onStartGame(win.gameKey);
    },
    [isLaunchBlocked, sectionName, viewMode, playSelectGameSound, onStartGame],
  );

  // Nothing until the first response lands: the alternative is a drawer that flashes "nobody has
  // won yet" for the length of the request. A jackpot that truly has no wins renders the drawer
  // with its empty state instead — the level was clickable, so silence would read as a broken tap.
  if (!isLoaded) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      data-device={deviceMode}
      data-testid={testIds.jackpots.statisticsOverlay(sectionName)}
    >
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        className={styles.drawer}
        data-testid={testIds.jackpots.statisticsDrawer(jackpotName)}
      >
        <JackpotStatisticsContent
          jackpotName={jackpotName}
          wins={visibleWins}
          header={
            <JackpotStatisticsHeader
              jackpotName={jackpotName}
              win={headerWin}
              sortMode={sortMode}
              isLaunchBlocked={isLaunchBlocked}
              onSelectGame={handleSelectGame}
            />
          }
          isLaunchBlocked={isLaunchBlocked}
          onSelectGame={handleSelectGame}
          scrollerRef={scrollerRef}
        />
        <JackpotStatisticsFooter
          jackpotName={jackpotName}
          levels={levels}
          selectedLevelName={selectedLevelName}
          onToggleLevel={handleToggleLevel}
          sortMode={sortMode}
          onSelectSort={handleSelectSort}
          onClose={onClose}
        />
      </div>
    </div>,
    document.body,
  );
};

export default JackpotStatisticsDrawer;
